import {
  applyGroup,
  applyUngroup,
  GROUP_REJECTION_MESSAGES,
  planGroup,
  planUngroup,
} from '../core/model/groups';
import { type LayerLocation, locateLayer, replaceSiblings } from '../core/model/tree';
import type { Layer } from '../core/model/types';
import type { AppContext } from './context';
import { orderedSelection } from './layers';

/**
 * Grouping and ungrouping, as app flows.
 *
 * The model half lives in `core/model/groups.ts`; this decides *when* it
 * happens and what the user is told. It is a module of its own because the
 * commands reach it through a dynamic import: grouping is a feature people use
 * once in a while, and the boot chunk should not carry it.
 *
 * A group is a folder (see docs/DESIGN-iteration-11.md §2.1): it owns a name,
 * a visibility flag and a lock, and its children keep their own geometry.
 * Grouping therefore cannot change a single pixel, and ungrouping puts
 * everything back exactly where it was.
 */

/** The active layer and the list it lives in. */
function activeLocation(ctx: AppContext): { layer: Layer; location: LayerLocation } | null {
  const { doc, activeLayerId } = ctx.store.getState();
  if (!activeLayerId) return null;
  const location = locateLayer(doc.layers, activeLayerId);
  if (!location) return null;
  const layer = location.siblings[location.index];
  if (!layer) return null;
  return { layer, location };
}

export function groupActiveLayer(ctx: AppContext): void {
  const { doc, commit, setActiveLayer, notify, selectedIds } = ctx.store.getState();
  // One selected layer keeps the Iteration 11 pair rule (active + the one
  // below, the muscle memory Ctrl+G built); two or more group exactly what is
  // selected, and planGroup refuses a set that spans two parents.
  const ids = selectedIds.size > 1 ? [...selectedIds] : (activeLocation(ctx)?.layer.id ?? null);
  if (!ids) return;
  const { plan, reason } = planGroup(doc, ids);
  if (!plan) {
    notify(GROUP_REJECTION_MESSAGES[reason ?? 'not-found'], 'warn');
    return;
  }
  const location = locateLayer(doc.layers, (Array.isArray(ids) ? ids[0] : ids) as string);
  if (!location) return;
  const next = {
    ...doc,
    layers: replaceSiblings(doc.layers, location, applyGroup(location.siblings, plan)),
  };
  commit('Group Layers', next);
  setActiveLayer(plan.group.id);
  notify(`Grouped ${plan.children.length} layers`);
}

/**
 * Ungroup: every selected group's children take its place, in the same order.
 * Leaves in the selection are ignored — ungrouping is something you do *to* a
 * group. One history entry, whatever the set did.
 */
export function ungroupActiveLayer(ctx: AppContext): void {
  const { doc, commit, assignSelection, notify } = ctx.store.getState();
  const picked = orderedSelection(ctx).filter((layer) => layer.type === 'group');
  if (picked.length === 0) {
    // Nothing in the selection is a group, so there is nothing to take apart.
    notify(GROUP_REJECTION_MESSAGES['not-a-group'], 'warn');
    return;
  }
  let layers = doc.layers;
  const freed: string[] = [];
  let blockedBy: string | null = null;
  for (const group of picked) {
    const { plan, reason } = planUngroup({ ...doc, layers }, group.id);
    if (!plan) {
      if (reason === 'locked') blockedBy = group.name;
      continue;
    }
    const location = locateLayer(layers, group.id);
    if (!location) continue;
    layers = replaceSiblings(layers, location, applyUngroup(location.siblings, plan));
    for (const child of plan.children) freed.push(child.id);
  }
  if (layers === doc.layers) {
    notify(blockedBy ? `“${blockedBy}” is locked` : 'Nothing to ungroup', 'warn');
    return;
  }
  commit(picked.length > 1 ? `Ungroup ${picked.length} Groups` : 'Ungroup Layers', {
    ...doc,
    layers,
  });
  // The freed children become the selection — ungrouping is a way of saying
  // "I want to work with these individually now".
  assignSelection(freed);
  notify(picked.length > 1 ? `Ungrouped ${picked.length} groups` : 'Ungrouped');
}
